import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useAuthenticationDataContext } from '../../Helpers/AuthenticationDataProvider';
import { useAuthorizationDataContext } from '../../Helpers/AuthorizationDataProvider';
import { useIntlWrapperContext } from '../../Helpers/IntlWrapperProvider';
import useFetchWrapper, { HttpResponse } from '../../Helpers/useFetchWrapper';
import { useOrcidHelper } from '../../Helpers/useOrcidHelper';
import useScoreHelper, { Score } from '../../Helpers/useScoreHelper';
import { Article } from './ArticlesTab/ArticlesTab.model';

import {
	Review,
	UserProfileModel,
	UserProfileResponse,
	UserRatingResponse,
} from './MyProfile.model';

const useUserProfile = () => {
	const [activeTabKey, setActiveTab] = useState('1');
	const [averageScore, setAverageScore] = useState<Score | undefined>(undefined);
	const [profile, setProfile] = useState<UserProfileModel>();
	const [profileResponse, setProfileResponse] = useState<UserProfileResponse>();
	const [reviews, setReviews] = useState<Review[]>([]);
	const [orcidUserId, setOrcidUserId] = useState<string>();
	const [showOrcidPopup, setShowOrcidPopup] = useState<boolean>(false);
	const [triggerArticlePopup, setTriggerArticlePopup] = useState<boolean>(false);
	const { get } = useFetchWrapper();
	const { rocketAuthToken, userId } = useAuthenticationDataContext();
	const { isAuthorized } = useAuthorizationDataContext();
	const { i18nCountries, getI18nLanguageName } = useIntlWrapperContext();
	const intl = useIntl();
	const history = useHistory();
	let { url } = useRouteMatch();
	const { getScoreByAverageRating } = useScoreHelper();

	const location: any = useLocation();
	const { activeTab } = location.state || {};

	const {
		getTokenFromURL,
		getUserInfo,
		redirectToAuthorizeOAuthURL} = useOrcidHelper();
	
	useEffect(() => {
		setActiveTab(activeTab || '1');
	}, []);

	useEffect(() => {
		const token = getTokenFromURL();
		if (token) {
			getOrcidUserId(token);
		}
	}, []);

	useEffect(() => {
		getUserProfile();
	}, [userId]);

	useEffect(() => {
		profile?.role === 'Mentor' && getUserRating();
	}, [profile]);

	const getOrcidUserId = async (token: string) => {
		const response = await getUserInfo(token);
		const userInfo = await response.json();
		const { sub } = userInfo;
		setOrcidUserId(sub);
		setShowOrcidPopup(true);
	}

	const formatDate = (date: string) => {
		return intl.formatDate(new Date(date), { month: 'long', year: 'numeric' });
	}

	const getUserRating = async () => {
		const response: HttpResponse<UserRatingResponse> = await get(`/review/rating/${userId}?page=1&size=5`);

		if (response.parsedBody) {
			const { averageScore, ratings } = response.parsedBody;

			setReviews(
				ratings.map(({ feedback, score, user }) => ({
					feedback,
					rating: score,
					reviewer: {
						imageId: user.imageId,
						isDeleted: user.isDeleted,
						name: `${user.firstName} ${user.lastName}`,
						userId: user.userId,
					},
				}))
			);
			setAverageScore(getScoreByAverageRating(averageScore) );
		}
	}
	
	const getUserProfile = async () => {
		const response: HttpResponse<UserProfileResponse> = await get(`/user/profile/${userId}`);

		if (response.parsedBody) {
			const {
				conference,
				currentPosition,
				education,
				firstName,
				imageId,
				introduction,
				isReachableByCall,
				isReachableByText,
				languages,
				lastName,
				location,
				mentoringScopes,
				pronouns,
				researchAreas,
				role,
				workExperience,
			} = response.parsedBody;

			const user: UserProfileModel = {
				conference,
				currentPosition,
				education,
				firstName,
				imageId,
				introduction,
				isReachableByCall,
				isReachableByText,
				languages: languages?.map((code) => getI18nLanguageName(code)),
				lastName,
				location,
				mentoringScopes,
				name: `${firstName} ${lastName} `,
				pronouns,
				researchAreas,
				role,
				workExperience,
			};

			setProfile(user);
			setProfileResponse(response.parsedBody)
		}
	}

	const navigateTo = (path: string, state?: any, search?: string) => {
		history.push({ pathname: path, search, state });
	}


	const onClickReviewer = (id: string) => {
		const path = userId !== id ? `/members-list/${id}` : '/my-profile';
		
    navigateTo(path);
	}

	const onChangeTab = (activeKey: string) => {
		setActiveTab(activeKey);
	}

	const toggleOrcidPopup = () => {
		setShowOrcidPopup(value => !value);
	}

	const onCloseOrcidPopup = () => {
		toggleOrcidPopup();
		history.push({ pathname: '/my-profile', state: { activeTab: "2" } });
		setActiveTab("2");
	}

	return {
		formatDate,
		getUserProfile,
		isAuthorized,
		navigateTo,
		onChangeTab,
		onClickReviewer,
		onCloseOrcidPopup,
		redirectToAuthorizeOAuthURL,
		setActiveTab,
		setTriggerArticlePopup,
		state: {
			activeTabKey,
			averageScore,
			i18nCountries,
			orcidUserId,
			profile,
			profileResponse,
			reviews,
			rocketAuthToken,
			showOrcidPopup,
			triggerArticlePopup,
		},
		toggleOrcidPopup,
		userId,
	}

}
export default useUserProfile;